/**
 * CycleActionButtons Component
 * 
 * A reusable component for displaying action buttons for billing cycles.
 * Supports different variants and conditional rendering based on cycle status.
 * 
 * @component
 * @example
 * ```tsx
 * <CycleActionButtons
 *   cycle={billingCycle}
 *   variant="compact"
 *   onInvoice={(cycle) => console.log("Invoice", cycle)}
 *   visibleStatuses={["Excluded", "SoCreated"]}
 * />
 * ```
 * 
 * @props
 * - cycle: BillingCycle - The billing cycle data
 * - variant?: "default" | "compact" - Display variant (default: "default")
 * - onInvoice?: (cycle: BillingCycle) => void - Optional callback for invoice action
 * - visibleStatuses?: string[] - Optional array of statuses where buttons should be visible
 * - labels?: { invoice?: string, reinvoice?: string } - Optional custom labels
 * 
 * @performance
 * - Optimized with useCallback for event handlers
 * - Conditional rendering to prevent unnecessary DOM elements
 * - Minimal re-renders through memoization
 */

import { useState, useCallback, useMemo } from 'react';

import { FileText } from 'lucide-react';

import { Button } from '@/components/evo/ui/button';
import { UserRole } from '@/config/roles.config';
import { BillingCycle } from './types';
import { InvoiceModal } from './CycleActionModals';

interface CycleActionButtonsProps {
  cycle: BillingCycle;
  variant?: 'default' | 'compact';
  onInvoice?: (cycle: BillingCycle) => void;
  visibleStatuses?: string[];
  labels?: {
    invoice?: string;
    reinvoice?: string;
  };
  onDataChanged?: () => void;
  userRole?: UserRole;
}

// Default statuses where buttons should be visible
const DEFAULT_VISIBLE_STATUSES = ['Excluded', 'SoCreated'];

// Default labels
const DEFAULT_LABELS = {
  invoice: 'Invoice',
  reinvoice: 'Reinvoice',
};

export function CycleActionButtons({
  cycle,
  variant = 'default',
  onInvoice,
  visibleStatuses = DEFAULT_VISIBLE_STATUSES,
  labels = DEFAULT_LABELS,
  onDataChanged,
  userRole,
}: CycleActionButtonsProps) {
  const isFullAccessOrAdminRole = userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN;
  // State: Modal visibility
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  // Memoized: Determine if buttons should be shown
  const shouldShowButtons = useMemo(() => {
    return cycle.processingStatus ? visibleStatuses.includes(cycle.processingStatus) : false;
  }, [cycle.processingStatus, visibleStatuses]);

  // Memoized: Determine button labels
  const buttonLabels = useMemo(() => {
    const isSoCreated = cycle.processingStatus === 'SoCreated';

    return {
      invoice: isSoCreated
        ? (labels.reinvoice || DEFAULT_LABELS.reinvoice)
        : (labels.invoice || DEFAULT_LABELS.invoice),
    };
  }, [cycle.processingStatus, labels]);

  // Optimized handlers with useCallback
  const handleInvoiceClick = useCallback(() => {
    if (onInvoice) {
      // If onInvoice callback is provided, use it (modal is lifted to parent)
      onInvoice(cycle);
    } else {
      // Otherwise, use internal modal state
      setInvoiceModalOpen(true);
    }
  }, [cycle, onInvoice]);

  const handleInvoiceModalChange = useCallback((open: boolean) => {
    setInvoiceModalOpen(open);
  }, []);

  // Early return if buttons should not be shown or user lacks permission
  if (!shouldShowButtons || !isFullAccessOrAdminRole) {
    return null;
  }

  // Render compact variant
  if (variant === 'compact') {
    return (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleInvoiceClick}
            className="h-7 px-2 text-xs w-full justify-start"
            title={buttonLabels.invoice}
          >
            <FileText className="h-3 w-3 mr-1" />
            {buttonLabels.invoice}
          </Button>

          {/* Only render InvoiceModal internally if onInvoice callback is NOT provided */}
          {!onInvoice && (
            <InvoiceModal
              open={invoiceModalOpen}
              onOpenChange={handleInvoiceModalChange}
              cycle={cycle}
              onDataChanged={onDataChanged}
            />
          )}
        </>
    );
  }

  // Render default variant
  return (
    <>
      <div className="flex gap-2 pt-2 border-t">
        <Button
          variant="outline"
          size="sm"
          onClick={handleInvoiceClick}
          className="flex-1"
        >
          <FileText className="h-4 w-4 mr-2" />
          {buttonLabels.invoice}
        </Button>
      </div>

      {/* Only render InvoiceModal internally if onInvoice callback is NOT provided */}
      {!onInvoice && (
        <InvoiceModal
          open={invoiceModalOpen}
          onOpenChange={handleInvoiceModalChange}
          cycle={cycle}
          onDataChanged={onDataChanged}
        />
      )}
    </>
  );
}
